import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useNode } from '@craftjs/core';
import { isBlankHtml } from '@/utils/functions';
import { AUTO_FIT_ROW, autoFitItemForColumns, normalizeColumns } from '../utils/autoFitRow';
import { htmlSeguro } from '../utils/htmlSeguro';

export type ProporcionGaleria = 'auto' | '1:1' | '4:3' | '16:9';

interface ImagenGaleria {
  src?: string;
  alt?: string;
  caption?: string;
}

interface GalleryBlockProps {
  images?: ImagenGaleria[];
  columns?: number;
  gap?: number;
  aspectRatio?: ProporcionGaleria;
  borderRadius?: number;
  showCaptions?: boolean;
  captionColor?: string;
  padding?: number;
}

export const PROPORCIONES_GALERIA: Record<ProporcionGaleria, string | undefined> = {
  auto: undefined,
  '1:1': '1 / 1',
  '4:3': '4 / 3',
  '16:9': '16 / 9',
};

/**
 * Galería de imágenes — render del sitio público.
 *
 * El bloque suelto equivalente a la sección `PhotoGallery`, que sigue existiendo
 * con su título y su fondo. Reusa los mismos helpers de fila (`autoFitRow`) que
 * ella, así una fila incompleta queda centrada en vez de pegada a la izquierda.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/GalleryBlock.tsx.
 */
export const GalleryBlock = ({
  images = [],
  columns = 3,
  gap = 16,
  aspectRatio = '4:3',
  borderRadius = 12,
  showCaptions = false,
  captionColor = '#6b7280',
  padding = 0,
}: GalleryBlockProps) => {
  const { connectors } = useNode();

  const cols = normalizeColumns(columns, 3);
  const claseItem = autoFitItemForColumns(cols);
  const separacion = Number.isFinite(Number(gap)) ? Math.max(0, Number(gap)) : 16;
  const proporcion = PROPORCIONES_GALERIA[aspectRatio];
  const visibles = (images ?? []).filter((img) => !!img?.src);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{
        width: '100%',
        padding: `${padding}px`,
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      <div
        className={AUTO_FIT_ROW}
        style={{ gap: `${separacion}px`, ['--bg-gap' as string]: `${separacion}px` }}
      >
        {visibles.map((img, i) => (
          <figure key={i} className={claseItem} style={{ margin: 0 }}>
            <Img sizes="(max-width: 768px) 100vw, 33vw"
              src={img.src}
              alt={img.alt || ''}
              className="block w-full"
              style={{
                borderRadius: `${borderRadius}px`,
                objectFit: 'cover',
                ...(proporcion ? { aspectRatio: proporcion, height: 'auto' } : {}),
              }}
            />
            {showCaptions && !isBlankHtml(img.caption) && (
              <figcaption
                className="mt-2 text-sm"
                style={{ color: captionColor }}
                dangerouslySetInnerHTML={htmlSeguro(img.caption || '')}
              />
            )}
          </figure>
        ))}
      </div>
    </div>
  );
};

GalleryBlock.craft = {
  displayName: 'GalleryBlock',
  props: {
    images: [],
    columns: 3,
    gap: 16,
    aspectRatio: '4:3',
    borderRadius: 12,
    showCaptions: false,
    captionColor: '#6b7280',
    padding: 0,
  },
};
